import { useState } from 'react'
import { ChevronDown, ChevronUp, Lightbulb, ListChecks } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Step } from '@/types'

interface StepWalkthroughProps {
  steps: Step[]
}

export function StepWalkthrough({ steps }: StepWalkthroughProps) {
  const [expandedStep, setExpandedStep] = useState<number | null>(0)

  return (
    <div className="space-y-2">
      {steps.map((step, idx) => {
        const isExpanded = expandedStep === idx

        return (
          <div key={step.number} className="relative">
            <div
              className={cn(
                'rounded-xl border transition-all duration-200',
                isExpanded
                  ? 'border-[var(--accent-blue)] bg-[var(--bg-primary)] shadow-[var(--shadow-sm)]'
                  : 'border-[var(--border)] bg-[var(--bg-primary)] hover:border-[var(--border-strong)]',
              )}
            >
              {/* Step header */}
              <button
                className="w-full flex items-center gap-4 p-4 text-left"
                onClick={() => setExpandedStep(isExpanded ? null : idx)}
                aria-expanded={isExpanded}
              >
                {/* Number circle */}
                <div
                  className={cn(
                    'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors',
                    isExpanded
                      ? 'bg-[var(--accent-blue)] text-white'
                      : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]',
                  )}
                >
                  {step.number}
                </div>

                <div className="flex-1 min-w-0">
                  <span
                    className={cn(
                      'font-medium text-sm transition-colors',
                      isExpanded ? 'text-[var(--accent-blue)]' : 'text-[var(--text-primary)]',
                    )}
                  >
                    {step.title}
                  </span>
                </div>

                {isExpanded ? (
                  <ChevronUp size={16} className="flex-shrink-0 text-[var(--accent-blue)]" />
                ) : (
                  <ChevronDown size={16} className="flex-shrink-0 text-[var(--text-muted)]" />
                )}
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 animate-slide-up">
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed pl-12">
                    {step.description}
                  </p>

                  {step.substeps && step.substeps.length > 0 && (
                    <div className="pl-12">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-secondary)] mb-2">
                        <ListChecks size={13} />
                        What to look for
                      </div>
                      <ul className="space-y-1.5">
                        {step.substeps.map((sub, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--accent-blue)] mt-2" />
                            {sub}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {step.tips && step.tips.length > 0 && (
                    <div className="pl-12">
                      <div className="rounded-lg bg-[var(--accent-orange-light)] border border-orange-100 dark:border-orange-900 p-3 space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--accent-orange)] mb-1">
                          <Lightbulb size={13} />
                          Partner tip
                        </div>
                        {step.tips.map((tip, i) => (
                          <p key={i} className="text-sm text-[var(--accent-orange-dark)] dark:text-orange-300 leading-relaxed">
                            {tip}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
